/**
 * Copyright © 2026 Adnuntius AS.
 */
import {SignalResourceListBindingService} from '../common/controller/signal-resource-list-binding.service';
import {DestroyRef, inject, Component, Inject, Input, OnInit, signal} from '@angular/core';
import {NgForm} from '@angular/forms';
import {UIRouter} from '@uirouter/core';

import template from './creative-bulk-upload-page.component.html';
import {ADVERTISING_SHARED} from '../advertising/advertising-shared';
import {applyHalfSizeDimensions, CreativeBulkUploadBehaviorService} from './creative-bulk-upload-setup';
import {CreativeBulkActionService} from './creative-bulk-action.service';
import {CreativeAttachModalService} from './creative-attach-modal.service';
import {CreativeCopyInBulkModalService} from './creative-copy-in-bulk-modal.service';
import {createFormSubmitState, exposeFormSubmitStateAccessors, FormSubmitState} from '../common/controller/form-submit-state';
import {asUrlGroupList, asUrlList} from './creative-url-utils';
import {TeamApiService} from '../admin/team/team-api.service';
import {LocalNetworkInfo} from '../../components/session/local-network-info';
import {CreativeApiService} from './creative-api.service';
import {LayoutApiService} from '../admin/layout/layout-api.service';
import {LibraryCreativeApiService} from '../library-creative/library-creative-api.service';
import {LineItemApiService} from '../line-item/line-item-api.service';
import {AssetService} from './asset/asset.service';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {compareById} from '../common/util/object-id-comparison';

import {NavigationHistoryService} from '../../components/util/navigation-history.service';

interface CreativeBulkUploadViewState {
  model: any;
  bulk: any;
  isPureMarketplace: boolean;
  allowsPoliticalAds: boolean;
  political: boolean;
  bulkCreatives: any[];
  view: string;
  perms: any;
  type: string;
  validTypes: Record<string, any>;
  validSubTypes: Record<string, {asArray?: string[]}>;
  creativeFields: any[];
  hasCreativeSet: boolean;
  attachSuffix: string;
  lineItemOption: any;
  creativeSetOption: any;
  defaultAssignmentType: string;
  allTeams: any[];
  bulkSelection: Record<string, any>;
  advertiserSpecified: boolean;
  advertiser: any;
  lineItem: any;
  lineItemSpecified: boolean;
  creativeSetFromParams: any;
  selected: string;
  thirdPartyLayouts: any[];
  politicalThirdPartyLayouts: any[];
  highImpactThirdPartyLayouts: any[];
  highImpactPoliticalThirdPartyLayouts: any[];
  imageLayouts: any[];
  politicalImageLayouts: any[];
  highImpactImageLayouts: any[];
  highImpactPoliticalImageLayouts: any[];
  videoLayouts: any[];
  politicalVideoLayouts: any[];
  highImpactVideoLayouts: any[];
  highImpactPoliticalVideoLayouts: any[];
  htmlLayouts: any[];
  politicalHtmlLayouts: any[];
  highImpactHtmlLayouts: any[];
  highImpactPoliticalHtmlLayouts: any[];
  allowedStandard: Record<string, any[]>;
  allowedPolitical: Record<string, any[]>;
  allowedHighImpact: Record<string, any[]>;
  allowedHighImpactPolitical: Record<string, any[]>;
  subTypes: string[];
  layoutQueryDone: boolean;
  bulkSubmitPending: boolean;
  bulkSubmitPromise: Promise<any> | null;
  hasError: boolean;
  bulkOrderErrors: any[];
  hasUpload: boolean;
  refreshCodeMirrorVar: boolean;
  incorrectMimeTypeFiles: any[] | null;
  filesFailedUpload: any[] | null;
  cannotProcess: any[];
  isNew: boolean;
  noValidAssets: boolean;
  loadingCreatives: boolean;
  selectableLayouts: any[];
}

@Component({
  standalone: true,
  imports: [...ADVERTISING_SHARED],
  selector: 'adn-creative-bulk-upload-page',
  template
})
export class CreativeBulkUploadPageComponent implements OnInit {
  private readonly listLifetime = inject(DestroyRef);
  [key: string]: any;

  private _creativeTypeDirect: string | undefined;
  private setupInitialized = false;
  private activeNgForm: NgForm | null = null;
  private readonly bulkViewState = signal<CreativeBulkUploadViewState>({} as CreativeBulkUploadViewState, {
    equal: () => false
  });

  submitState: FormSubmitState = createFormSubmitState();

  readonly codeMirrorOpts = {lineNumbers: true, mode: 'htmlmixed', smartIndent: false};

  @Input()
  set creativeType(value: string) {
    this._creativeTypeDirect = value;
  }

  get creativeType(): string {
    return this._creativeTypeDirect;
  }

  constructor(
    @Inject(AssetService) private readonly Asset: any,
    @Inject(LayoutApiService) private readonly Layout: LayoutApiService,
    private readonly teamApi: TeamApiService,
    @Inject(LineItemApiService) private readonly LineItem: LineItemApiService,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    @Inject(LibraryCreativeApiService) private readonly LibraryCreative: LibraryCreativeApiService,
    @Inject(CreativeApiService) private readonly Creative: CreativeApiService,
    private readonly listBinding: SignalResourceListBindingService,
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    private readonly creativeBulkActionService: CreativeBulkActionService,
    private readonly creativeAttachModalService: CreativeAttachModalService,
    private readonly creativeCopyInBulkModalService: CreativeCopyInBulkModalService,
    private readonly uiRouter: UIRouter,
    private readonly navigationHistory: NavigationHistoryService,
    private readonly creativeBulkUploadBehaviorService: CreativeBulkUploadBehaviorService,
    private readonly localNetworkInfo: LocalNetworkInfo = new LocalNetworkInfo()
  ) {
    exposeFormSubmitStateAccessors(this, this.submitState);
  }

  publishBulkViewState(): void {
    this.bulkViewState.set({...this.bulkViewState()});
  }

  get model(): any { return this.readBulkView('model'); }
  set model(value: any) { this.writeBulkView('model', value); }
  get bulk(): any { return this.readBulkView('bulk'); }
  set bulk(value: any) { this.writeBulkView('bulk', value); }
  get isPureMarketplace(): boolean { return this.readBulkView('isPureMarketplace'); }
  set isPureMarketplace(value: boolean) { this.writeBulkView('isPureMarketplace', value); }
  get allowsPoliticalAds(): boolean { return this.readBulkView('allowsPoliticalAds'); }
  set allowsPoliticalAds(value: boolean) { this.writeBulkView('allowsPoliticalAds', value); }
  get political(): boolean { return this.readBulkView('political'); }
  set political(value: boolean) { this.writeBulkView('political', value); }
  get bulkCreatives(): any[] { return this.readBulkView('bulkCreatives'); }
  set bulkCreatives(value: any[]) { this.writeBulkView('bulkCreatives', value); }
  get view(): string { return this.readBulkView('view'); }
  set view(value: string) { this.writeBulkView('view', value); }
  get perms(): any { return this.readBulkView('perms'); }
  set perms(value: any) { this.writeBulkView('perms', value); }
  get type(): string { return this.readBulkView('type'); }
  set type(value: string) { this.writeBulkView('type', value); }
  get validTypes(): Record<string, any> { return this.readBulkView('validTypes'); }
  set validTypes(value: Record<string, any>) { this.writeBulkView('validTypes', value); }
  get validSubTypes(): Record<string, {asArray?: string[]}> { return this.readBulkView('validSubTypes'); }
  set validSubTypes(value: Record<string, {asArray?: string[]}>) { this.writeBulkView('validSubTypes', value); }
  get creativeFields(): any[] { return this.readBulkView('creativeFields'); }
  set creativeFields(value: any[]) { this.writeBulkView('creativeFields', value); }
  get hasCreativeSet(): boolean { return this.readBulkView('hasCreativeSet'); }
  set hasCreativeSet(value: boolean) { this.writeBulkView('hasCreativeSet', value); }
  get attachSuffix(): string { return this.readBulkView('attachSuffix'); }
  set attachSuffix(value: string) { this.writeBulkView('attachSuffix', value); }
  get lineItemOption(): any { return this.readBulkView('lineItemOption'); }
  set lineItemOption(value: any) { this.writeBulkView('lineItemOption', value); }
  get creativeSetOption(): any { return this.readBulkView('creativeSetOption'); }
  set creativeSetOption(value: any) { this.writeBulkView('creativeSetOption', value); }
  get defaultAssignmentType(): string { return this.readBulkView('defaultAssignmentType'); }
  set defaultAssignmentType(value: string) { this.writeBulkView('defaultAssignmentType', value); }
  get allTeams(): any[] { return this.readBulkView('allTeams'); }
  set allTeams(value: any[]) { this.writeBulkView('allTeams', value); }
  get bulkSelection(): Record<string, any> { return this.readBulkView('bulkSelection'); }
  set bulkSelection(value: Record<string, any>) { this.writeBulkView('bulkSelection', value); }
  get advertiserSpecified(): boolean { return this.readBulkView('advertiserSpecified'); }
  set advertiserSpecified(value: boolean) { this.writeBulkView('advertiserSpecified', value); }
  get advertiser(): any { return this.readBulkView('advertiser'); }
  set advertiser(value: any) { this.writeBulkView('advertiser', value); }
  get lineItem(): any { return this.readBulkView('lineItem'); }
  set lineItem(value: any) { this.writeBulkView('lineItem', value); }
  get lineItemSpecified(): boolean { return this.readBulkView('lineItemSpecified'); }
  set lineItemSpecified(value: boolean) { this.writeBulkView('lineItemSpecified', value); }
  get creativeSetFromParams(): any { return this.readBulkView('creativeSetFromParams'); }
  set creativeSetFromParams(value: any) { this.writeBulkView('creativeSetFromParams', value); }
  get selected(): string { return this.readBulkView('selected'); }
  set selected(value: string) { this.writeBulkView('selected', value); }
  get thirdPartyLayouts(): any[] { return this.readBulkView('thirdPartyLayouts'); }
  set thirdPartyLayouts(value: any[]) { this.writeBulkView('thirdPartyLayouts', value); }
  get politicalThirdPartyLayouts(): any[] { return this.readBulkView('politicalThirdPartyLayouts'); }
  set politicalThirdPartyLayouts(value: any[]) { this.writeBulkView('politicalThirdPartyLayouts', value); }
  get highImpactThirdPartyLayouts(): any[] { return this.readBulkView('highImpactThirdPartyLayouts'); }
  set highImpactThirdPartyLayouts(value: any[]) { this.writeBulkView('highImpactThirdPartyLayouts', value); }
  get highImpactPoliticalThirdPartyLayouts(): any[] { return this.readBulkView('highImpactPoliticalThirdPartyLayouts'); }
  set highImpactPoliticalThirdPartyLayouts(value: any[]) { this.writeBulkView('highImpactPoliticalThirdPartyLayouts', value); }
  get imageLayouts(): any[] { return this.readBulkView('imageLayouts'); }
  set imageLayouts(value: any[]) { this.writeBulkView('imageLayouts', value); }
  get politicalImageLayouts(): any[] { return this.readBulkView('politicalImageLayouts'); }
  set politicalImageLayouts(value: any[]) { this.writeBulkView('politicalImageLayouts', value); }
  get highImpactImageLayouts(): any[] { return this.readBulkView('highImpactImageLayouts'); }
  set highImpactImageLayouts(value: any[]) { this.writeBulkView('highImpactImageLayouts', value); }
  get highImpactPoliticalImageLayouts(): any[] { return this.readBulkView('highImpactPoliticalImageLayouts'); }
  set highImpactPoliticalImageLayouts(value: any[]) { this.writeBulkView('highImpactPoliticalImageLayouts', value); }
  get videoLayouts(): any[] { return this.readBulkView('videoLayouts'); }
  set videoLayouts(value: any[]) { this.writeBulkView('videoLayouts', value); }
  get politicalVideoLayouts(): any[] { return this.readBulkView('politicalVideoLayouts'); }
  set politicalVideoLayouts(value: any[]) { this.writeBulkView('politicalVideoLayouts', value); }
  get highImpactVideoLayouts(): any[] { return this.readBulkView('highImpactVideoLayouts'); }
  set highImpactVideoLayouts(value: any[]) { this.writeBulkView('highImpactVideoLayouts', value); }
  get highImpactPoliticalVideoLayouts(): any[] { return this.readBulkView('highImpactPoliticalVideoLayouts'); }
  set highImpactPoliticalVideoLayouts(value: any[]) { this.writeBulkView('highImpactPoliticalVideoLayouts', value); }
  get htmlLayouts(): any[] { return this.readBulkView('htmlLayouts'); }
  set htmlLayouts(value: any[]) { this.writeBulkView('htmlLayouts', value); }
  get politicalHtmlLayouts(): any[] { return this.readBulkView('politicalHtmlLayouts'); }
  set politicalHtmlLayouts(value: any[]) { this.writeBulkView('politicalHtmlLayouts', value); }
  get highImpactHtmlLayouts(): any[] { return this.readBulkView('highImpactHtmlLayouts'); }
  set highImpactHtmlLayouts(value: any[]) { this.writeBulkView('highImpactHtmlLayouts', value); }
  get highImpactPoliticalHtmlLayouts(): any[] { return this.readBulkView('highImpactPoliticalHtmlLayouts'); }
  set highImpactPoliticalHtmlLayouts(value: any[]) { this.writeBulkView('highImpactPoliticalHtmlLayouts', value); }
  get allowedStandard(): Record<string, any[]> { return this.readBulkView('allowedStandard'); }
  set allowedStandard(value: Record<string, any[]>) { this.writeBulkView('allowedStandard', value); }
  get allowedPolitical(): Record<string, any[]> { return this.readBulkView('allowedPolitical'); }
  set allowedPolitical(value: Record<string, any[]>) { this.writeBulkView('allowedPolitical', value); }
  get allowedHighImpact(): Record<string, any[]> { return this.readBulkView('allowedHighImpact'); }
  set allowedHighImpact(value: Record<string, any[]>) { this.writeBulkView('allowedHighImpact', value); }
  get allowedHighImpactPolitical(): Record<string, any[]> { return this.readBulkView('allowedHighImpactPolitical'); }
  set allowedHighImpactPolitical(value: Record<string, any[]>) { this.writeBulkView('allowedHighImpactPolitical', value); }
  get subTypes(): string[] { return this.readBulkView('subTypes'); }
  set subTypes(value: string[]) { this.writeBulkView('subTypes', value); }
  get layoutQueryDone(): boolean { return this.readBulkView('layoutQueryDone'); }
  set layoutQueryDone(value: boolean) { this.writeBulkView('layoutQueryDone', value); }
  get bulkSubmitPending(): boolean { return this.readBulkView('bulkSubmitPending'); }
  set bulkSubmitPending(value: boolean) { this.writeBulkView('bulkSubmitPending', value); }
  get bulkSubmitPromise(): Promise<any> | null { return this.readBulkView('bulkSubmitPromise'); }
  set bulkSubmitPromise(value: Promise<any> | null) { this.writeBulkView('bulkSubmitPromise', value); }
  get hasError(): boolean { return this.readBulkView('hasError'); }
  set hasError(value: boolean) { this.writeBulkView('hasError', value); }
  get bulkOrderErrors(): any[] { return this.readBulkView('bulkOrderErrors'); }
  set bulkOrderErrors(value: any[]) { this.writeBulkView('bulkOrderErrors', value); }
  get hasUpload(): boolean { return this.readBulkView('hasUpload'); }
  set hasUpload(value: boolean) { this.writeBulkView('hasUpload', value); }
  get refreshCodeMirrorVar(): boolean { return this.readBulkView('refreshCodeMirrorVar'); }
  set refreshCodeMirrorVar(value: boolean) { this.writeBulkView('refreshCodeMirrorVar', value); }
  get incorrectMimeTypeFiles(): any[] | null { return this.readBulkView('incorrectMimeTypeFiles'); }
  set incorrectMimeTypeFiles(value: any[] | null) { this.writeBulkView('incorrectMimeTypeFiles', value); }
  get filesFailedUpload(): any[] | null { return this.readBulkView('filesFailedUpload'); }
  set filesFailedUpload(value: any[] | null) { this.writeBulkView('filesFailedUpload', value); }
  get cannotProcess(): any[] { return this.readBulkView('cannotProcess'); }
  set cannotProcess(value: any[]) { this.writeBulkView('cannotProcess', value); }
  get isNew(): boolean { return this.readBulkView('isNew'); }
  set isNew(value: boolean) { this.writeBulkView('isNew', value); }
  get noValidAssets(): boolean { return this.readBulkView('noValidAssets'); }
  set noValidAssets(value: boolean) { this.writeBulkView('noValidAssets', value); }
  get loadingCreatives(): boolean { return this.readBulkView('loadingCreatives'); }
  set loadingCreatives(value: boolean) { this.writeBulkView('loadingCreatives', value); }
  get selectableLayouts(): any[] { return this.readBulkView('selectableLayouts'); }
  set selectableLayouts(value: any[]) { this.writeBulkView('selectableLayouts', value); }

  get formSubmitPristine(): boolean {
    return this.activeNgForm?.pristine ?? true;
  }

  ngOnInit(): void {
    if (this.setupInitialized || !this.creativeType) {
      return;
    }
    this.setupInitialized = true;
    this.creativeBulkUploadBehaviorService.initialize(this, {
      creativeType: this.creativeType,
      navigationHistory: this.navigationHistory,
      routeParams: this.uiRouter.globals.params,
      Asset: this.Asset,
      Layout: this.Layout,
      teamApi: this.teamApi,
      LineItem: this.LineItem,
      LocalNetworkProfile: this.LocalNetworkProfile,
      LibraryCreative: this.LibraryCreative,
      Creative: this.Creative,
      listBinding: this.listBinding,
      listLifetime: this.listLifetime,
      LocalUserPermissions: this.LocalUserPermissions,
      creativeBulkActionService: this.creativeBulkActionService,
      creativeAttachModalService: this.creativeAttachModalService,
      creativeCopyInBulkModalService: this.creativeCopyInBulkModalService,
      localNetworkInfo: this.localNetworkInfo
    });
  }

  readonly compareById = compareById;

  private readonly objectKeysCache = new WeakMap<object, string[]>();

  objectKeys(obj: any): string[] {
    if (!obj) {
      return [];
    }
    const keys = Object.keys(obj);
    const cached = this.objectKeysCache.get(obj);
    if (cached && cached.length === keys.length && cached.every((key, i) => key === keys[i])) {
      return cached;
    }
    this.objectKeysCache.set(obj, keys);
    return keys;
  }

  isTeamSelected(creative: any, teamId: string): boolean {
    return !!(creative?.teams && creative.teams.indexOf(teamId) > -1);
  }

  toggleTeam(creative: any, teamId: string, event: Event): void {
    const checked = !!(event.target as HTMLInputElement).checked;
    creative.teams = creative.teams || [];
    if (checked && creative.teams.indexOf(teamId) < 0) {
      creative.teams.push(teamId);
    } else if (!checked) {
      creative.teams = creative.teams.filter((id: string) => id !== teamId);
    }
  }

  toggleHalfSize(creative: any, event: Event): void {
    const halfSize = !!(event.target as HTMLInputElement).checked;
    applyHalfSizeDimensions(creative, halfSize);
    this.activeNgForm?.control.markAsDirty();
  }

  firstLineItemKey(creative: any): string {
    return creative?.lineItems ? Object.keys(creative.lineItems)[0] : '';
  }

  get currentSubTypes(): string[] {
    return (this.validSubTypes && this.validSubTypes[this.type] && this.validSubTypes[this.type].asArray) || [];
  }

  constraintUrls(creative: any): string[] {
    return asUrlList(creative?.constraintsToUrls);
  }

  assetHtmlUrlGroups(creative: any): string[][] {
    return asUrlGroupList(creative?.assetHtmlUrls);
  }

  setBulkForm(form: NgForm): string {
    this.activeNgForm = form;
    this.submitState.ngForm = form;
    if (this.setForm) {
      this.setForm(form);
    }
    return '';
  }

  private readBulkView<K extends keyof CreativeBulkUploadViewState>(key: K): CreativeBulkUploadViewState[K] {
    return this.bulkViewState()[key];
  }

  private writeBulkView<K extends keyof CreativeBulkUploadViewState>(
    key: K,
    value: CreativeBulkUploadViewState[K]
  ): void {
    const state = this.bulkViewState();
    if (!Object.is(state[key], value)) {
      this.bulkViewState.set({...state, [key]: value});
    }
  }
}
